<template>
  <!-- Loading Overlay - Mobile Optimized  -->
  <div
    v-if="isInitialLoading"
    class="fixed inset-0 bg-white z-50 flex items-center justify-center p-4"
  >
    <div class="text-center max-w-sm mx-auto">
      <div class="relative mb-4 sm:mb-6">
        <!-- Single Spinner -->
        <div
          class="w-16 h-16 sm:w-20 sm:h-20 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"
        ></div>
      </div>
      <div class="space-y-2">
        <h3 class="text-lg sm:text-xl font-semibold text-gray-800">
          {{ loadingMessage }}
        </h3>
        <p class="text-xs sm:text-sm text-gray-600">
          Please wait while we prepare your dashboard...
        </p>
        <!-- Loading Progress Dots  -->
        <div class="flex justify-center space-x-1 mt-4">
          <div class="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
          <div
            class="w-2 h-2 bg-blue-600 rounded-full animate-pulse opacity-75"
          ></div>
          <div
            class="w-2 h-2 bg-blue-600 rounded-full animate-pulse opacity-50"
          ></div>
        </div>
      </div>
    </div>
  </div>

  <!-- Main Content  -->
  <div
    v-else
    class="min-h-screen bg-gray-50"
  >
    <div
      class="max-w-none xl:max-w-[1400px] 2xl:max-w-[1600px] mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8"
    >
      <!-- Header - Mobile Responsive  -->
      <div class="mb-6 sm:mb-8">
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
          Attendance History
        </h1>
        <p class="text-sm sm:text-base text-gray-600">
          Track and manage student attendance with ease
        </p>
      </div>

      <!-- Filter Section - Mobile First  -->
      <div
        class="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-6"
      >
        <!-- Mobile Filter Toggle  -->
        <div class="flex items-center justify-between mb-2 sm:hidden">
          <h3 class="text-lg font-semibold text-gray-900">Filters</h3>
          <button
            @click="showMobileFilters = !showMobileFilters"
            class="p-2 text-gray-600"
          >
            <svg
              v-if="!showMobileFilters"
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              ></path>
            </svg>
            <svg
              v-else
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        <!-- Filter Content -->
        <div
          :class="[
            'sm:block transition-all duration-300',
            showMobileFilters ? 'block' : 'hidden sm:block',
          ]"
        >
          <div
            class="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center"
          >
            <!-- Search Input -->
            <div class="relative group flex-1 sm:max-w-xs">
              <Search
                class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-blue-500 transition-colors"
              />
              <input
                ref="searchInput"
                v-model="search"
                type="text"
                placeholder="Search by student name..."
                :disabled="isLoading || isTableLoading"
                class="w-full pl-10 pr-4 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              />
            </div>

            <!-- Class Filter  -->
            <div class="relative group sm:w-48 flex-shrink-0">
              <Users
                class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-blue-500 transition-colors pointer-events-none"
              />
              <select
                v-model="selectedClass"
                :disabled="isLoading || isTableLoading"
                class="w-full pl-10 pr-10 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed appearance-none text-sm sm:text-base"
              >
                <option value="">All Classes</option>
                <option
                  v-for="cls in availableClasses.filter(
                    (c) => c.name.toLowerCase() !== 'passout',
                  )"
                  :key="cls._id"
                  :value="cls._id"
                  :title="`${cls.name}${
                    cls.section ? ` - Section ${cls.section}` : ''
                  }`"
                >
                  {{ cls.name }}{{ cls.section ? ` - ${cls.section}` : "" }}
                </option>
              </select>
              <ChevronDown
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none"
              />
            </div>

            <!-- Date Range Selector  -->
            <div class="relative group sm:w-60 flex-shrink-0">
              <Calendar
                class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-blue-500 transition-colors pointer-events-none"
              />
              <select
                v-model="dateRangeOption"
                :disabled="isLoading || isTableLoading"
                @change="handleDateRangeChange"
                class="w-full pl-10 pr-10 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed appearance-none text-sm sm:text-base"
              >
                <option value="today">Today</option>
                <option value="yesterday">yesterday</option>
                <option value="last7days">Last 7 Days</option>
                <option value="last30days">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
              <ChevronDown
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none"
              />
            </div>

            <!-- Custom Date Range Inputs (shown when custom is selected)  -->
            <div
              v-if="dateRangeOption === 'custom'"
              class="flex flex-col sm:flex-row gap-2 sm:gap-3"
            >
              <div class="relative group sm:w-48 flex-shrink-0">
                <Calendar
                  class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-blue-500 transition-colors"
                />
                <input
                  v-model="startDate"
                  type="date"
                  :disabled="isLoading || isTableLoading"
                  placeholder="Start Date"
                  class="w-full pl-10 pr-4 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                />
              </div>
              <div class="relative group sm:w-48 flex-shrink-0">
                <Calendar
                  class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-blue-500 transition-colors"
                />
                <input
                  v-model="endDate"
                  type="date"
                  :disabled="isLoading || isTableLoading"
                  placeholder="End Date"
                  class="w-full pl-10 pr-4 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                />
              </div>
            </div>

            <!-- Buttons Container -->
            <div class="flex flex-wrap gap-2 sm:gap-3">
              <!-- All Records / Mine Only Toggle -->
              <button
                :class="[
                  'px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 border',
                  isMineOnly
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                  isLoading || isTableLoading
                    ? 'opacity-50 cursor-not-allowed'
                    : '',
                ]"
                @click="
                  isMineOnly = !isMineOnly;
                  fetchFilteredHistory();
                "
                :disabled="isLoading || isTableLoading"
              >
                {{ isMineOnly ? "Mine Only" : "All Records" }}
              </button>

              <!-- Refresh Button -->
              <button
                :class="[
                  'px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 border bg-green-600 text-white border-green-600 hover:bg-green-700',
                  isLoading || isTableLoading
                    ? 'opacity-50 cursor-not-allowed'
                    : '',
                ]"
                @click="fetchFilteredHistory()"
                :disabled="isLoading || isTableLoading"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Cards - Mobile Responsive Grid (only for single day view) -->
      <div
        v-if="dateRangeOption === 'today' && !isDateRangeLoading"
        class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-2 sm:mb-3"
      >
        <!-- Present Students -->
        <div
          class="bg-white border border-gray-200 rounded-md p-2 sm:p-3 relative"
        >
          <div
            v-if="isStatsLoading"
            class="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-md"
          >
            <div
              class="w-4 h-4 sm:w-5 sm:h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"
            ></div>
          </div>
          <div class="flex items-center justify-between mb-1 sm:mb-2">
            <div class="p-1 sm:p-2 bg-green-100 rounded-md">
              <UserCheck class="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            </div>
            <span
              class="text-xs font-medium bg-gray-100 text-gray-600 px-1 sm:px-2 py-0.5 rounded-full"
            >
              {{ displayDate }}
            </span>
          </div>
          <h3 class="text-sm sm:text-base font-semibold mb-1 text-gray-900">
            Present Students
          </h3>
          <p class="text-xl sm:text-2xl font-bold mb-1 text-gray-900">
            <template v-if="!isStatsLoading">
              {{
                selectedClass
                  ? stats.today?.classWise?.[String(selectedClass)]?.present ||
                    0
                  : stats.today?.overall?.totalPresent || 0
              }}
            </template>
            <template v-else>--</template>
          </p>
          <p class="text-gray-600 text-xs">
            Out of
            <template v-if="!isStatsLoading">
              {{
                selectedClass
                  ? stats.today?.classWise?.[String(selectedClass)]
                      ?.totalStudents || 0
                  : stats.today?.overall?.totalStudents || 0
              }}
            </template>
            <template v-else>--</template>
            students
          </p>
        </div>

        <!-- Absent Students -->
        <div
          class="bg-white border border-gray-200 rounded-md p-2 sm:p-3 relative"
        >
          <div
            v-if="isStatsLoading"
            class="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-md"
          >
            <div
              class="w-4 h-4 sm:w-5 sm:h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"
            ></div>
          </div>
          <div class="flex items-center justify-between mb-1 sm:mb-2">
            <div class="p-1 sm:p-2 bg-red-100 rounded-md">
              <UserX class="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <span
              class="text-xs font-medium bg-gray-100 text-gray-600 px-1 sm:px-2 py-0.5 rounded-full"
            >
              {{ displayDate }}
            </span>
          </div>
          <h3 class="text-sm sm:text-base font-semibold mb-1 text-gray-900">
            Absent Students
          </h3>
          <p class="text-xl sm:text-2xl font-bold mb-1 text-gray-900">
            <template v-if="!isStatsLoading">
              {{
                selectedClass
                  ? stats.today?.classWise?.[String(selectedClass)]?.absent || 0
                  : stats.today?.overall?.totalAbsent || 0
              }}
            </template>
            <template v-else>--</template>
          </p>
          <p class="text-gray-600 text-xs">
            <template v-if="!isStatsLoading"
              >{{ getAttendancePercentage() }}%</template
            >
            <template v-else>--%</template>
            attendance rate
          </p>
        </div>

        <!-- Late Students -->
        <div
          class="bg-white border border-gray-200 rounded-md p-2 sm:p-3 relative"
        >
          <div
            v-if="isStatsLoading"
            class="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-md"
          >
            <div
              class="w-4 h-4 sm:w-5 sm:h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"
            ></div>
          </div>
          <div class="flex items-center justify-between mb-1 sm:mb-2">
            <div class="p-1 sm:p-2 bg-yellow-100 rounded-md">
              <Clock class="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
            </div>
            <span
              class="text-xs font-medium bg-gray-100 text-gray-600 px-1 sm:px-2 py-0.5 rounded-full"
            >
              {{ displayDate }}
            </span>
          </div>
          <h3 class="text-sm sm:text-base font-semibold mb-1 text-gray-900">
            Late
          </h3>
          <p class="text-xl sm:text-2xl font-bold mb-1 text-gray-900">
            <template v-if="!isStatsLoading">
              {{
                selectedClass
                  ? stats.today?.classWise?.[String(selectedClass)]?.late || 0
                  : stats.today?.overall?.totalLate || 0
              }}
            </template>
            <template v-else>--</template>
          </p>
          <p class="text-gray-600 text-xs">late students</p>
        </div>

        <!-- Average Attendance -->
        <div
          class="bg-white border border-gray-200 rounded-md p-2 sm:p-3 relative"
        >
          <div
            v-if="isStatsLoading"
            class="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-md"
          >
            <div
              class="w-4 h-4 sm:w-5 sm:h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"
            ></div>
          </div>
          <div class="flex items-center justify-between mb-1 sm:mb-2">
            <div class="p-1 sm:p-2 bg-blue-100 rounded-md">
              <TrendingUp class="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <span
              class="text-xs font-medium bg-gray-100 text-gray-600 px-1 sm:px-2 py-0.5 rounded-full"
              >7 Days</span
            >
          </div>
          <h3 class="text-sm sm:text-base font-semibold mb-1 text-gray-900">
            Avg Attendance
          </h3>
          <p class="text-xl sm:text-2xl font-bold mb-1 text-gray-900">
            <template v-if="!isStatsLoading"
              >{{ getWeeklyAverage() }}%</template
            >
            <template v-else>--%</template>
          </p>
          <p class="text-gray-600 text-xs">Past week average</p>
        </div>
      </div>

      <!-- Date Range Stats -->
      <div
        v-if="dateRangeOption !== 'today' && !isDateRangeLoading"
        class="bg-white rounded-lg shadow-sm border border-gray-200 p-2 sm:p-3 mb-2 sm:mb-3"
      >
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-semibold text-gray-900">
            Date Range Summary
          </h3>
          <div class="text-xs text-gray-500">
            {{ formatDateRange(startDate, endDate) }}
          </div>
        </div>

        <div class="flex justify-between gap-2">
          <div class="flex-1 text-center p-2 bg-blue-50 rounded-md">
            <div class="text-lg font-bold text-blue-600">
              {{ totalResults }}
            </div>
            <div class="text-xs text-gray-600">Total Records</div>
          </div>

          <div class="flex-1 text-center p-2 bg-green-50 rounded-md">
            <div class="text-lg font-bold text-green-600">
              {{ presentCount }}
            </div>
            <div class="text-xs text-gray-600">Present</div>
          </div>

          <div class="flex-1 text-center p-2 bg-red-50 rounded-md">
            <div class="text-lg font-bold text-red-600">{{ absentCount }}</div>
            <div class="text-xs text-gray-600">Absent</div>
          </div>
        </div>
      </div>

      <!-- Rest of the component remains the same... Attendance History Table -
      Mobile Responsive -->
      <div
        class="bg-white border border-gray-200 rounded-lg overflow-hidden relative"
      >
        <!-- Table Loading Overlay -->
        <div
          v-if="isTableLoading || isDateRangeLoading"
          class="absolute inset-0 bg-white bg-opacity-90 z-10 flex items-center justify-center"
        >
          <div class="text-center">
            <!-- Dots -->
            <div class="flex items-center justify-center space-x-2 mb-3">
              <span
                class="w-3 h-3 sm:w-4 sm:h-4 bg-blue-600 rounded-full animate-bounce"
              ></span>
              <span
                class="w-3 h-3 sm:w-4 sm:h-4 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.2s]"
              ></span>
              <span
                class="w-3 h-3 sm:w-4 sm:h-4 bg-blue-300 rounded-full animate-bounce [animation-delay:-0.4s]"
              ></span>
            </div>

            <!-- Message -->
            <p class="text-xs sm:text-sm font-medium text-gray-600">
              {{
                isDateRangeLoading
                  ? dateRangeLoadingMessage
                  : tableLoadingMessage
              }}
            </p>
          </div>
        </div>

        <!-- Table Header -->
        <div class="p-4 sm:p-6 border-b border-gray-200 hidden sm:block">
          <div
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0"
          >
            <div>
              <h3 class="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                Recent Attendance Records
              </h3>
              <p class="text-sm text-gray-600">
                Latest attendance entries and updates
              </p>
            </div>
            <div class="flex items-center gap-2">
              <!-- CSV Export Button -->
              <button
                @click="exportToCSV"
                :disabled="
                  isLoading ||
                  isTableLoading ||
                  isDateRangeLoading ||
                  !history ||
                  history.length === 0
                "
                class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Export to CSV"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  ></path>
                </svg>
                Export CSV
              </button>
            </div>
          </div>
        </div>

        <!-- Mobile CSV Export Button -->
        <div class="block sm:hidden p-4 border-b border-gray-200">
          <button
            @click="exportToCSV"
            :disabled="
              isLoading ||
              isTableLoading ||
              isDateRangeLoading ||
              !history ||
              history.length === 0
            "
            class="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              ></path>
            </svg>
            Export to CSV
          </button>
        </div>

        <!-- Content Area -->
        <div v-if="!isTableLoading && !isDateRangeLoading">
          <!-- Mobile View - Cards for single date only -->
          <div
            class="block md:hidden"
            v-if="dateRangeOption === 'today'"
          >
            <!-- Mobile Cards -->
            <div class="px-0 sm:px-4 space-y-4">
              <div
                v-for="entry in sortedHistory"
                :key="entry._id"
                class="bg-white rounded-lg p-4 border border-gray-200 shadow-md hover:shadow-lg transform transition-all duration-200 hover:scale-[1.01] mx-2"
              >
                <!-- Card Header -->
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center gap-2">
                    <Calendar class="w-4 h-4 text-gray-400" />
                    <span
                      class="text-xs sm:text-sm font-semibold text-gray-900"
                    >
                      {{ formatDate(entry.date) }}
                    </span>
                  </div>
                  <span
                    :class="getStatusClass(entry.status)"
                    class="inline-flex items-center gap-1 px-2 py-1 text-[10px] sm:text-xs font-bold rounded-full"
                  >
                    <div
                      class="w-2 h-2 rounded-full"
                      :class="getStatusDotClass(entry.status)"
                    ></div>
                    {{ capitalizeStatus(entry.status) }}
                  </span>
                </div>

                <!-- Student Info -->
                <div class="flex items-center space-x-3 mb-3">
                  <div class="flex items-center justify-center">
                    <!-- ✅ Show photo if available -->
                    <img
                      v-if="entry.studentPhoto"
                      :src="entry.studentPhoto"
                      alt="Student Photo"
                      class="h-10 w-10 sm:h-10 sm:w-10 rounded-full object-cover"
                    />

                    <!-- 🔄 Otherwise, show initials -->
                    <div
                      v-else
                      class="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm"
                    >
                      {{ getInitials(entry.studentName) }}
                    </div>
                  </div>

                  <div class="flex flex-col">
                    <span class="text-sm font-semibold text-gray-900">
                      {{ entry.studentName || "N/A" }}
                    </span>
                    <span class="text-xs text-gray-500">
                      Roll: {{ entry.rollNumber || "N/A" }}
                    </span>
                  </div>
                </div>

                <!-- Additional Info -->
                <div class="flex justify-between text-xs text-gray-600">
                  <div class="flex items-center gap-1">
                    <User class="w-3 h-3" />
                    <span>{{
                      entry.teacherName || entry.device?.deviceName || "N/A"
                    }}</span>
                  </div>
                  <div class="flex items-center gap-1">
                    <Clock class="w-3 h-3" />
                    <span>
                      {{ formatTime(entry.updatedAt || entry.createdAt) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              <div
                v-if="!history || history.length === 0"
                class="text-center py-12"
              >
                <div
                  class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <ClipboardList class="w-8 h-8 text-blue-500" />
                </div>
                <h3 class="text-lg font-bold text-gray-900 mb-2">
                  No attendance records found
                </h3>
                <p class="text-sm text-gray-600">
                  Try adjusting your filters or check back later.
                </p>
              </div>
            </div>
          </div>

          <!-- Mobile View - Table for date range only -->
          <div
            class="block md:hidden max-w-[290px]"
            v-if="dateRangeOption !== 'today'"
          >
            <!-- Enhanced mobile table with full horizontal scroll -->
            <div class="relative">
              <!-- Scroll indicator shadows -->
              <div
                class="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"
              ></div>
              <div
                class="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"
              ></div>

              <!-- Scrollable table container -->
              <div
                class="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
              >
                <table class="w-full text-sm">
                  <!-- Date Range View Headers -->
                  <thead
                    class="bg-gray-50 border-b border-gray-200 sticky top-0 z-20"
                  >
                    <tr>
                      <th
                        class="px-2 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider min-w-[60px]"
                      >
                        Roll
                      </th>
                      <th
                        class="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider min-w-[120px]"
                      >
                        Name
                      </th>
                      <th
                        v-for="date in dateRangeArray"
                        :key="date"
                        class="px-2 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider min-w-[80px]"
                      >
                        <div class="flex flex-col">
                          <span class="text-[10px]">{{
                            formatDateHeader(date).split(" ")[0]
                          }}</span>
                          <span class="text-[10px] text-gray-500">{{
                            formatDateHeader(date).split(" ")[1]
                          }}</span>
                        </div>
                      </th>
                      <th
                        class="px-2 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider min-w-[80px]"
                      >
                        Status
                      </th>
                      <th
                        class="px-2 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider min-w-[100px]"
                      >
                        Teacher or Device
                      </th>
                    </tr>
                  </thead>

                  <!-- Date Range View Body -->
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr
                      v-for="student in groupedRangeData"
                      :key="student.studentId"
                      class="hover:bg-gray-50 transition-colors"
                    >
                      <td
                        class="px-2 py-3 whitespace-nowrap text-xs font-semibold text-gray-900"
                      >
                        {{ student.rollNo || "N/A" }}
                      </td>
                      <td class="px-3 py-3 whitespace-nowrap">
                        <div class="flex items-center gap-2">
                          <img
                            v-if="student.studentPhoto"
                            :src="student.studentPhoto"
                            class="h-6 w-6 rounded-full object-cover"
                          />
                          <div
                            v-else
                            class="h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0"
                          >
                            <span class="text-[10px] font-bold text-white">
                              {{ getInitials(student.studentName) }}
                            </span>
                          </div>

                          <span
                            class="text-xs font-semibold text-gray-900 truncate max-w-[80px]"
                            :title="student.studentName"
                          >
                            {{ student.studentName }}
                          </span>
                        </div>
                      </td>
                      <td
                        v-for="date in dateRangeArray"
                        :key="date"
                        class="px-2 py-3 text-center whitespace-nowrap"
                      >
                        <span
                          v-if="student.attendance[date]"
                          :class="
                            getStatusClass(student.attendance[date].status)
                          "
                          class="inline-flex items-center justify-center w-6 h-6 text-[10px] font-bold rounded-full"
                        >
                          {{ getStatusIcon(student.attendance[date].status) }}
                        </span>
                        <span
                          v-else
                          class="text-gray-400 text-xs"
                          >-</span
                        >
                      </td>
                      <td class="px-2 py-3 whitespace-nowrap text-center">
                        <span
                          class="text-xs font-medium truncate max-w-[120px]"
                          :class="{
                            'text-green-600 font-bold ':
                              student.overallStatus === 'Present',
                            'text-red-600 font-bold ':
                              student.overallStatus === 'Absent',
                            'text-gray-700':
                              student.overallStatus !== 'Present' &&
                              student.overallStatus !== 'Absent',
                          }"
                          v-html="student.overallStatus"
                          :title="student.overallStatus.replace(/<[^>]+>/g, '')"
                        >
                        </span>
                      </td>

                      <td class="px-2 py-3 whitespace-nowrap">
                        <span
                          class="text-xs font-medium text-gray-700 truncate max-w-[80px]"
                          :title="
                            getStudentDeviceName(student) ||
                            student.lastTeacher ||
                            'N/A'
                          "
                        >
                          {{
                            getStudentDeviceName(student) ||
                            student.lastTeacher ||
                            "N/A"
                          }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Scroll hint for mobile users -->
              <div class="flex justify-center mt-2 text-xs text-gray-500">
                <span class="flex items-center gap-1">
                  <svg
                    class="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M7 16l-4-4m0 0l4-4m-4 4h18"
                    ></path>
                  </svg>
                  Scroll horizontally to view all dates
                  <svg
                    class="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    ></path>
                  </svg>
                </span>
              </div>
            </div>

            <!-- Mobile Empty State for date range -->
            <div
              v-if="!groupedRangeData || groupedRangeData.length === 0"
              class="text-center py-12"
            >
              <div
                class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <ClipboardList class="w-8 h-8 text-blue-500" />
              </div>
              <h3 class="text-lg font-bold text-gray-900 mb-2">
                No attendance records found
              </h3>
              <p class="text-sm text-gray-600">
                Try adjusting your filters or check back later.
              </p>
            </div>
          </div>

          <!-- Desktop View - Table -->
          <div class="hidden md:block overflow-x-auto">
            <!-- Actual Table -->
            <table class="w-full">
              <!-- Single Date View Headers -->
              <thead
                v-if="dateRangeOption === 'today'"
                class="bg-gray-50 border-b border-gray-200"
              >
                <tr>
                  <th
                    class="px-4 lg:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
                  >
                    Date
                  </th>
                  <th
                    class="px-2 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider min-w-[60px]"
                  >
                    Roll
                  </th>
                  <th
                    class="px-4 lg:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
                  >
                    Student
                  </th>
                  <th
                    class="px-4 lg:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    class="px-4 lg:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
                  >
                    Teacher or Device
                  </th>
                  <th
                    class="px-4 lg:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
                  >
                    Time
                  </th>
                </tr>
              </thead>

              <!-- Date Range View Headers -->
              <thead
                v-else
                class="bg-gray-50 border-b border-gray-200"
              >
                <tr>
                  <th
                    class="px-4 lg:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
                  >
                    Roll No
                  </th>
                  <th
                    class="px-4 lg:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
                  >
                    Name
                  </th>
                  <th
                    v-for="date in dateRangeArray"
                    :key="date"
                    class="px-2 lg:px-3 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider min-w-[100px]"
                  >
                    {{ formatDateHeader(date) }}
                  </th>
                  <th
                    class="px-4 lg:px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    class="px-4 lg:px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider"
                  >
                    Teacher or Device
                  </th>
                </tr>
              </thead>

              <!-- Single Date View Body -->
              <tbody
                v-if="dateRangeOption === 'today'"
                class="bg-white divide-y divide-gray-200"
              >
                <tr
                  v-for="entry in sortedHistory"
                  :key="entry._id"
                  class="hover:bg-gray-50 transition-colors"
                >
                  <td class="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center gap-2">
                      <Calendar class="w-4 h-4 text-gray-400" />
                      <span class="text-sm font-semibold text-gray-900">{{
                        formatDate(entry.date)
                      }}</span>
                    </div>
                  </td>
                  <td
                    class="px-2 py-3 whitespace-nowrap text-xs font-semibold text-gray-900"
                  >
                    {{ entry.rollNumber || "N/A" }}
                  </td>
                  <td class="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center gap-3">
                      <div
                        class="h-8 w-8 lg:h-10 lg:w-10 rounded-full overflow-hidden flex items-center justify-center bg-blue-600"
                      >
                        <!-- Show photo if available -->
                        <img
                          v-if="entry.studentPhoto"
                          :src="entry.studentPhoto"
                          alt="Student Photo"
                          class="h-full w-full object-cover"
                        />

                        <!-- Fallback to initials -->
                        <span
                          v-else
                          class="text-xs lg:text-sm font-bold text-white"
                        >
                          {{ getInitials(entry.studentName) }}
                        </span>
                      </div>

                      <span class="text-sm font-semibold text-gray-900">{{
                        entry.studentName || "N/A"
                      }}</span>
                    </div>
                  </td>
                  <td class="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <span
                      :class="getStatusClass(entry.status)"
                      class="inline-flex items-center gap-2 px-2 lg:px-3 py-1 lg:py-2 text-xs font-bold rounded-full"
                    >
                      <div
                        class="w-2 h-2 rounded-full"
                        :class="getStatusDotClass(entry.status)"
                      ></div>
                      {{ capitalizeStatus(entry.status) }}
                    </span>
                  </td>
                  <td class="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center gap-2">
                      <User class="w-4 h-4 text-gray-400" />
                      <span class="text-sm font-medium text-gray-700">
                        {{
                          entry.teacherName || entry.device?.deviceName || "N/A"
                        }}
                      </span>
                    </div>
                  </td>
                  <td class="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center gap-2">
                      <Clock class="w-4 h-4 text-gray-400" />
                      <span class="text-sm font-medium text-gray-700">
                        {{ formatTime(entry.updatedAt || entry.createdAt) }}
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>

              <!-- Date Range View Body -->
              <tbody
                v-else
                class="bg-white divide-y divide-gray-200"
              >
                <tr
                  v-for="student in groupedRangeData"
                  :key="student.studentId"
                  class="hover:bg-gray-50 transition-colors"
                >
                  <td class="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <span class="text-sm font-semibold text-gray-900">
                      {{ student.rollNo || "N/A" }}
                    </span>
                  </td>
                  <td class="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center gap-3">
                      <div
                        class="h-8 w-8 lg:h-10 lg:w-10 rounded-full overflow-hidden flex items-center justify-center bg-blue-600"
                      >
                        <!-- Show photo if available -->
                        <img
                          v-if="student.studentPhoto"
                          :src="student.studentPhoto"
                          alt="Student Photo"
                          class="h-full w-full object-cover"
                        />

                        <!-- Fallback to initials -->
                        <span
                          v-else
                          class="text-xs lg:text-sm font-bold text-white"
                        >
                          {{ getInitials(student.studentName) }}
                        </span>
                      </div>

                      <span class="text-sm font-semibold text-gray-900">
                        {{ student.studentName || "N/A" }}
                      </span>
                    </div>
                  </td>
                  <td
                    v-for="date in dateRangeArray"
                    :key="date"
                    class="px-2 lg:px-3 py-4 text-center whitespace-nowrap"
                  >
                    <span
                      v-if="student.attendance[date]"
                      :class="getStatusClass(student.attendance[date].status)"
                      class="inline-flex items-center justify-center w-8 h-8 text-xs font-bold rounded-full"
                    >
                      {{ getStatusIcon(student.attendance[date].status) }}
                    </span>
                    <span
                      v-else
                      class="text-gray-400 text-xs"
                      >-</span
                    >
                  </td>
                  <td class="px-4 lg:px-6 py-4 whitespace-nowrap text-center">
                    <span
                      class="text-sm font-medium truncate max-w-[150px]"
                      :class="{
                        'text-green-600 font-bold ':
                          student.overallStatus === 'Present',
                        'text-red-600 font-bold ':
                          student.overallStatus === 'Absent',
                        'text-gray-700':
                          student.overallStatus !== 'Present' &&
                          student.overallStatus !== 'Absent',
                      }"
                      v-html="student.overallStatus"
                      :title="student.overallStatus.replace(/<[^>]+>/g, '')"
                    >
                    </span>
                  </td>

                  <td class="px-4 lg:px-6 py-4 whitespace-nowrap">
                    <span class="text-sm font-medium text-gray-700">
                      {{
                        getStudentDeviceName(student) ||
                        student.lastTeacher ||
                        "N/A"
                      }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Desktop Empty State -->
            <div
              v-if="!history || history.length === 0"
              class="text-center py-16"
            >
              <div
                class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <ClipboardList class="w-10 h-10 text-blue-500" />
              </div>
              <h3 class="text-xl font-bold text-gray-900 mb-2">
                No attendance records found
              </h3>
              <p class="text-gray-600">
                Try adjusting your filters or check back later.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination Section - Mobile Responsive (only for single day view) -->
      <div
        v-if="dateRangeOption === 'today' && !isDateRangeLoading"
        class="flex flex-col sm:flex-row items-center justify-between mt-6 sm:mt-8 space-y-4 sm:space-y-0 px-2"
      >
        <!-- Results summary -->
        <div
          class="text-xs sm:text-sm text-gray-600 font-medium text-center sm:text-left"
        >
          <template v-if="!isTableLoading">
            Showing
            <span class="font-bold text-gray-900">
              {{ (currentPage - 1) * limit + 1 }}
            </span>
            to
            <span class="font-bold text-gray-900">
              {{ Math.min(currentPage * limit, totalResults) }}
            </span>
            of
            <span class="font-bold text-gray-900">
              {{ totalResults }}
            </span>
            results
          </template>
          <template v-else>
            <div
              class="flex items-center gap-2 justify-center sm:justify-start"
            >
              <div
                class="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"
              ></div>
              <span>Loading results...</span>
            </div>
          </template>
        </div>

        <!-- Pagination buttons -->
        <div class="flex items-center gap-1">
          <!-- Previous button -->
          <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1 || isTableLoading"
            class="px-2 sm:px-3 py-2 text-xs sm:text-sm font-semibold text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[36px] min-w-[36px] border border-gray-300"
          >
            <ChevronLeft class="w-4 h-4" />
          </button>

          <!-- Numbered buttons -->
          <template
            v-for="page in totalPages"
            :key="page"
          >
            <button
              @click="goToPage(page)"
              :disabled="isTableLoading"
              :class="[
                'px-2 sm:px-3 py-2 text-xs sm:text-sm rounded-lg transition-all duration-200 min-h-[36px] min-w-[36px] border',
                page === currentPage
                  ? 'font-bold bg-blue-600 text-white border-blue-600'
                  : 'font-semibold text-gray-600 hover:text-blue-600 hover:bg-gray-50 border-gray-300',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              ]"
            >
              {{ page }}
            </button>
          </template>

          <!-- Next button -->
          <button
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === totalPages || isTableLoading"
            class="px-2 sm:px-3 py-2 text-xs sm:text-sm font-semibold text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[36px] min-w-[36px] border border-gray-300"
          >
            <ChevronRight class="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch, nextTick } from "vue";
  import { useStore } from "vuex";
  import {
    Search,
    Calendar,
    ChevronDown,
    UserCheck,
    UserX,
    Clock,
    TrendingUp,
    User,
    ClipboardList,
    ChevronLeft,
    ChevronRight,
    Users,
  } from "lucide-vue-next";

  const store = useStore();

  // Loading States - Enhanced with date range loading
  const isInitialLoading = ref(false);
  const isLoading = ref(false);
  const isStatsLoading = ref(false);
  const isTableLoading = ref(false);
  const isSearching = ref(false);
  const isDateRangeLoading = ref(false); // New loading state for date range changes
  const loadingMessage = ref("Loading dashboard...");
  const tableLoadingMessage = ref("Loading attendance records...");
  const dateRangeLoadingMessage = ref("Loading date range data..."); // New loading message

  // Mobile state
  const showMobileFilters = ref(true);

  // Form filter fields
  const searchInput = ref(null);
  const search = ref("");
  const selectedClass = ref("");
  const selectedDate = ref(new Date().toISOString().split("T")[0]);

  // New date range option selector
  const dateRangeOption = ref("today");
  const startDate = ref(new Date().toISOString().split("T")[0]);
  const endDate = ref(new Date().toISOString().split("T")[0]);

  const isMineOnly = ref(false);
  const currentPage = ref(1);

  // Getters from Vuex
  const history = computed(() => store.getters.getAttendanceHistory);
  const stats = computed(() => store.getters.getAttendanceStats);
  const pagination = computed(() => store.getters.getAttendancePagination);
  const totalPages = computed(() => pagination.value.pages ?? 0);
  const totalResults = computed(() => pagination.value.total || 0);
  const limit = computed(() => pagination.value.limit || 20);
  const availableClasses = computed(() => store.getters.allClasses || []);

  // Computed for showing Today / Yesterday / Date
  const displayDate = computed(() => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const todayStr = today.toISOString().split("T")[0];
    const yesterdayStr = yesterday.toISOString().split("T")[0];

    if (selectedDate.value === todayStr) {
      return "Today";
    } else if (selectedDate.value === yesterdayStr) {
      return "Yesterday";
    } else {
      return formatDate(selectedDate.value);
    }
  });

  const presentCount = computed(() => {
    return history.value.filter((record) => record.status === "present").length;
  });

  const absentCount = computed(() => {
    return history.value.filter((record) => record.status === "absent").length;
  });

  const dateRangeArray = computed(() => {
    if (dateRangeOption.value === "today" || !startDate.value || !endDate.value)
      return [];

    const dates = [];
    const start = new Date(startDate.value);
    const end = new Date(endDate.value);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d).toISOString().split("T")[0]);
    }

    return dates;
  });

  const groupedRangeData = computed(() => {
    if (dateRangeOption.value === "today" || !history.value.length) return [];

    const studentMap = new Map();

    history.value.forEach((record) => {
      const studentId = record.studentId || record._id;
      const dateKey = new Date(record.date).toISOString().split("T")[0];

      if (!studentMap.has(studentId)) {
        studentMap.set(studentId, {
          studentId,
          studentName: record.studentName,
          rollNo: record.rollNumber,
          studentPhoto: record.studentPhoto || record.photo || null,
          attendance: {},
          lastTeacher: record.teacherName,
          lastTime: record.updatedAt || record.createdAt,

          overallStatus: "Mixed",
        });
      }

      const student = studentMap.get(studentId);
      student.attendance[dateKey] = {
        status: record.status,
        teacher: record.teacherName,
        deviceName: record.device?.deviceName || null,
        time: formatTime(record.createdAt),
      };

      const recordTime = record.updatedAt || record.createdAt;

      if (
        !student.lastTime ||
        new Date(recordTime) > new Date(student.lastTime)
      ) {
        student.lastTeacher = record.teacherName;
        student.lastTime = recordTime;
      }
    });

    // Calculate overall status for each student
    studentMap.forEach((student) => {
      const attendanceRecords = Object.values(student.attendance);
      const presentCount = attendanceRecords.filter(
        (a) => a.status === "present",
      ).length;
      const totalDays = dateRangeArray.value.length;

      if (presentCount === totalDays) {
        student.overallStatus = "Present";
      } else if (presentCount === 0) {
        student.overallStatus = "Absent";
      } else {
        student.overallStatus = `${presentCount} day${
          presentCount > 1 ? "s" : ""
        } (<span class="text-green-600 font-bold">present</span>) out of ${totalDays} day${
          totalDays > 1 ? "s" : ""
        }`;
      }
    });

    return Array.from(studentMap.values()).sort((a, b) =>
      compareRollNumbers(a.rollNo, b.rollNo),
    );
  });

  const sortedHistory = computed(() => {
    if (!history.value) return [];
    return [...history.value].sort((a, b) =>
      compareRollNumbers(a.rollNumber, b.rollNumber),
    );
  });

  // CSV Export Function
  const exportToCSV = () => {
    if (!history.value || history.value.length === 0) {
      alert("No data available to export");
      return;
    }

    let csvContent = "";
    let filename = "";

    if (dateRangeOption.value === "today") {
      // Single day export
      csvContent = "Date,Roll Number,Student Name,Status,Teacher,Time\n";

      sortedHistory.value.forEach((entry) => {
        const row = [
          formatDate(entry.date),
          entry.rollNumber || "N/A",
          `"${entry.studentName || "N/A"}"`,
          capitalizeStatus(entry.status),
          `"${entry.teacherName || "N/A"}"`,
          formatTime(entry.createdAt),
        ].join(",");
        csvContent += row + "\n";
      });

      filename = `attendance_${selectedDate.value}.csv`;
    } else {
      // Date range export
      const headers = [
        "Roll Number",
        "Student Name",
        ...dateRangeArray.value,
        "Overall Status",
        "Last Teacher",
      ];
      csvContent = headers.join(",") + "\n";

      groupedRangeData.value.forEach((student) => {
        const row = [
          student.rollNo || "N/A",
          `"${student.studentName}"`,
          ...dateRangeArray.value.map((date) => {
            const attendance = student.attendance[date];
            return attendance ? getStatusIcon(attendance.status) : "-";
          }),
          `"${student.overallStatus.replace(/<[^>]+>/g, "")}"`,
          `"${student.lastTeacher || "N/A"}"`,
        ];
        csvContent += row.join(",") + "\n";
      });

      filename = `attendance_${startDate.value}_to_${endDate.value}.csv`;
    }

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Methods
  const getInitials = (name) => {
    if (!name || typeof name !== "string") return "NA";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "present":
        return "bg-green-100 text-green-800 border border-green-200";
      case "absent":
        return "bg-red-100 text-red-800 border border-red-200";
      case "late":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  const getStatusDotClass = (status) => {
    switch (status) {
      case "present":
        return "bg-green-500";
      case "absent":
        return "bg-red-500";
      case "late":
        return "bg-yellow-500";
      default:
        return "bg-gray-500";
    }
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1) : "N/A";
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getAttendancePercentage = () => {
    const classKey = String(selectedClass.value);
    const classStats = stats.value?.today?.classWise?.[classKey];
    if (classStats && classStats.totalStudents > 0) {
      return Math.round((classStats.present / classStats.totalStudents) * 100);
    }
    const overall = stats.value?.today?.overall;
    if (!overall || overall.totalStudents === 0) return 0;
    return Math.round((overall.totalPresent / overall.totalStudents) * 100);
  };

  const getWeeklyAverage = () => {
    const daily = stats.value?.daily;
    if (!daily || daily.length === 0) return 0;
    const totalDays = daily.slice(0, 7);
    const avgPercentage =
      totalDays.reduce((acc, day) => {
        const total = day.totalPresent + day.totalAbsent;
        return acc + (total > 0 ? (day.totalPresent / total) * 100 : 0);
      }, 0) / totalDays.length;
    return Math.round(avgPercentage);
  };

  const formatDateHeader = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "present":
        return "P";
      case "absent":
        return "A";
      case "late":
        return "L";
      default:
        return "?";
    }
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return "";
    const startFormatted = new Date(start).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const endFormatted = new Date(end).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return start === end
      ? startFormatted
      : `${startFormatted} - ${endFormatted}`;
  };

  // Enhanced handle date range change with proper loading states
  const handleDateRangeChange = async () => {
    // Set loading state immediately
    isDateRangeLoading.value = true;
    dateRangeLoadingMessage.value = "Updating date range...";

    const today = new Date().toISOString().split("T")[0];

    switch (dateRangeOption.value) {
      case "today": {
        selectedDate.value = today;
        startDate.value = today;
        endDate.value = today;
        dateRangeLoadingMessage.value = "Loading today's records...";
        break;
      }
      case "yesterday": {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yDate = yesterday.toISOString().split("T")[0];
        selectedDate.value = yDate;
        startDate.value = yDate;
        endDate.value = yDate;
        dateRangeLoadingMessage.value = "Loading yesterday's records...";
        break;
      }
      case "last7days": {
        const last7Days = new Date();
        last7Days.setDate(last7Days.getDate() - 6);
        startDate.value = last7Days.toISOString().split("T")[0];
        endDate.value = today;
        dateRangeLoadingMessage.value = "Loading last 7 days...";
        break;
      }
      case "last30days": {
        const last30Days = new Date();
        last30Days.setDate(last30Days.getDate() - 29);
        startDate.value = last30Days.toISOString().split("T")[0];
        endDate.value = today;
        dateRangeLoadingMessage.value = "Loading last 30 days...";
        break;
      }
      case "custom": {
        dateRangeLoadingMessage.value = "Loading custom date range...";
        break;
      }
    }

    currentPage.value = 1;

    try {
      // Fetch data with proper loading states
      await fetchFilteredHistory();

      if (dateRangeOption.value === "today") {
        await fetchStats();
      }
    } catch (error) {
      console.error("Error handling date range change:", error);
    } finally {
      // Small delay to prevent flashing
      setTimeout(() => {
        isDateRangeLoading.value = false;
      }, 300);
    }
  };

  // Fetch attendance and stats on mount
  onMounted(async () => {
    isInitialLoading.value = true;
    loadingMessage.value = "Initializing dashboard...";
    try {
      await store.dispatch("fetchAllClasses");
      await Promise.all([fetchStats(), fetchFilteredHistory()]);
    } finally {
      isInitialLoading.value = false;
    }
  });

  // Watch filters with debouncing for search
  let searchTimeout;
  watch(search, async () => {
    clearTimeout(searchTimeout);
    isSearching.value = true;
    searchTimeout = setTimeout(async () => {
      currentPage.value = 1;
      await fetchFilteredHistory();
      await nextTick();
      if (searchInput.value) {
        searchInput.value.focus();
      }
    }, 300);
  });

  watch([selectedClass, selectedDate, isMineOnly, startDate, endDate], () => {
    // Don't trigger if date range is already loading
    if (!isDateRangeLoading.value) {
      currentPage.value = 1;
      fetchFilteredHistory();
      if (dateRangeOption.value === "today") {
        fetchStats();
      }
    }
  });

  watch(
    () => pagination.value.page,
    (val) => {
      currentPage.value = val;
    },
  );

  // Fetch stats with filters
  async function fetchStats() {
    isStatsLoading.value = true;
    try {
      const filters = {};
      if (selectedClass.value) {
        filters.classFilter = selectedClass.value;
      }
      if (dateRangeOption.value === "today" && selectedDate.value) {
        const formattedDate =
          typeof selectedDate.value === "string"
            ? selectedDate.value
            : new Date(selectedDate.value).toISOString().split("T")[0];
        filters.date = formattedDate;
      }
      await store.dispatch("fetchAttendanceStats", filters);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      isStatsLoading.value = false;
    }
  }

  // Enhanced fetch data with better loading state management
  async function fetchFilteredHistory() {
    const isInitialLoad = currentPage.value === 1 && history.value.length === 0;

    // Don't set table loading if date range is already loading
    if (!isDateRangeLoading.value) {
      if (isInitialLoad && !isInitialLoading.value) {
        isTableLoading.value = true;
        tableLoadingMessage.value = "Loading attendance records...";
      } else if (!isInitialLoading.value) {
        isTableLoading.value = true;
        tableLoadingMessage.value =
          currentPage.value === 1
            ? "Filtering records..."
            : `Loading page ${currentPage.value}...`;
      }
    }

    try {
      const filters = {
        page: currentPage.value,
        search: search.value || "",
        class: selectedClass.value || "",
      };

      // Apply limit only if single day view
      if (dateRangeOption.value === "today") {
        filters.limit = limit.value;
      }

      // Handle date filtering based on mode
      if (dateRangeOption.value !== "today") {
        if (startDate.value) filters.startDate = startDate.value;
        if (endDate.value) filters.endDate = endDate.value;
      } else {
        if (selectedDate.value) {
          filters.startDate = selectedDate.value;
          filters.endDate = selectedDate.value;
        }
      }

      if (isMineOnly.value) {
        filters.self = true;
      }

      await store.dispatch("fetchAttendanceHistory", filters);
    } catch (error) {
      console.error("Error fetching attendance history:", error);
    } finally {
      if (!isDateRangeLoading.value) {
        isTableLoading.value = false;
      }
      isSearching.value = false;
      isLoading.value = false;
    }
  }

  // Pagination navigation
  function goToPage(page) {
    if (
      page >= 1 &&
      page <= totalPages.value &&
      !isTableLoading.value &&
      !isDateRangeLoading.value
    ) {
      currentPage.value = page;
      isTableLoading.value = true;
      tableLoadingMessage.value = `Loading page ${page}...`;
      fetchFilteredHistory();
    }
  }
  function getStudentDeviceName(student) {
    if (!student.attendance) return null;

    for (const key in student.attendance) {
      const rec = student.attendance[key];
      if (rec?.deviceName) {
        return rec.deviceName;
      }
    }
    return null;
  }
  function compareRollNumbers(a, b) {
    if (!a) return 1;
    if (!b) return -1;

    const regex = /^([a-zA-Z]*)([-\s]*)(\d+)$/;

    const matchA = a.toString().trim().match(regex);
    const matchB = b.toString().trim().match(regex);

    // If both match pattern like D-1001
    if (matchA && matchB) {
      const prefixA = matchA[1];
      const prefixB = matchB[1];

      const numA = parseInt(matchA[3], 10);
      const numB = parseInt(matchB[3], 10);

      // First sort by prefix (D, A, B...)
      if (prefixA !== prefixB) {
        return prefixA.localeCompare(prefixB);
      }

      // Then by number
      return numA - numB;
    }

    // fallback natural string sort
    return a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }
</script>

<style scoped>
  /* Custom animations */
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  .animate-spin {
    animation: spin 1s linear infinite;
  }
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
  /* Custom scrollbar */
  .overflow-x-auto::-webkit-scrollbar {
    height: 6px;
  }
  .overflow-x-auto::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  .overflow-x-auto::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  .overflow-x-auto::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  /* Custom select arrow */
  select {
    background-image: none;
  }
  /* Disabled state improvements */
  .disabled\:opacity-50:disabled {
    opacity: 0.5;
  }
  .disabled\:cursor-not-allowed:disabled {
    cursor: not-allowed;
  }
  /* Mobile-specific optimizations */
  @media (max-width: 640px) {
    /* Ensure minimum touch targets */
    button {
      min-height: 44px;
      min-width: 44px;
    }
    input,
    select {
      min-height: 44px;
    }
    /* Optimize text sizes for mobile */
    .text-xs {
      font-size: 0.75rem;
      line-height: 1rem;
    }
    .text-sm {
      font-size: 0.875rem;
      line-height: 1.25rem;
    }
  }
  /* Tablet optimizations */
  @media (min-width: 641px) and (max-width: 1023px) {
    /* Adjust spacing for tablet */
    .p-4 {
      padding: 1.5rem;
    }
    .gap-3 {
      gap: 0.75rem;
    }
  }
  /* Accessibility improvements */
  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
</style>
